/**
 * Chart Renderer for PDF Export
 * 
 * Draws horizontal bar charts for all Market Data report types:
 * - Projected Patients (single bar by location/care setting)
 * - Shared Affiliation (stacked bar: affiliated vs non-affiliated)
 * - Payors (stacked bar: up to 3 payors + other)
 */

import { 
  getLocationTypeColor, 
  LOCATION_TYPES_FOR_LEGEND,
  PDF_SETTINGS,
  getSettingsByType,
  getColorsByType,
  getLegendByType,
} from './pdfStyles';

// Colors for Shared Affiliation chart
export const AFFILIATION_COLORS = {
  affiliated: [32, 131, 244],      // Blue - matches CSS rgba(32, 131, 244, 0.7)
  nonAffiliated: [255, 97, 120],   // Red/pink - matches CSS rgba(255, 97, 120, 0.7)
};

// Default payor colors matching the on-screen chart (ColorUtil.COLORS)
export const DEFAULT_PAYOR_COLORS = [
  [30, 136, 229],   // Blue - Medicare
  [67, 160, 71],    // Green - Medicaid  
  [255, 167, 38],   // Orange - third payor
  [227, 227, 227],  // Gray (Other)
];

/**
 * Draws a generic legend at the top of the chart
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} items - Array of {name, color} objects
 * @param {number} x - Starting X position
 * @param {number} y - Starting Y position
 * @param {number} maxWidth - Maximum width available
 * @param {string} pdfType - 'slideshow' or 'printable'
 * @returns {number} - New Y position after legend
 */
export const drawChartLegend = (doc, items, x, y, maxWidth, pdfType = 'printable') => {
  const settings = getSettingsByType(pdfType);
  const boxSize = pdfType === 'slideshow' ? 10 : 8;
  const gap = pdfType === 'slideshow' ? 6 : 6;
  let currentX = x;
  let currentY = y;
  
  doc.setFontSize(settings.chart.fontSize.legend);
  
  items.forEach((item, idx) => {
    const labelWidth = doc.getTextWidth(item.name);
    const itemWidth = boxSize + 4 + labelWidth + gap;
    
    // Wrap to next line if needed
    if (currentX + itemWidth > x + maxWidth && idx > 0) {
      currentX = x;
      currentY += pdfType === 'slideshow' ? 14 : 12;
    }
    
    // Draw color box
    doc.setFillColor(...item.color);
    doc.rect(currentX, currentY, boxSize, boxSize, 'F');
    
    // Draw label
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(item.name, currentX + boxSize + 4, currentY + (pdfType === 'slideshow' ? 8 : 6));
    
    currentX += itemWidth + (pdfType === 'slideshow' ? 10 : 8);
  });
  
  return currentY + (pdfType === 'slideshow' ? 16 : 14);
};

/**
 * Draw location type legend (for Projected Patients)
 */
export const drawLocationTypeLegend = (doc, x, y, maxWidth, pdfType = 'printable') => {
  const legendItems = getLegendByType(pdfType);
  return drawChartLegend(doc, legendItems, x, y, maxWidth, pdfType);
};

/**
 * Draw affiliation legend (for Shared Affiliation)
 */
export const drawAffiliationLegend = (doc, x, y, maxWidth, pdfType = 'printable') => {
  const items = [
    { name: 'Affiliated', color: AFFILIATION_COLORS.affiliated },
    { name: 'Non-Affiliated', color: AFFILIATION_COLORS.nonAffiliated },
  ];
  return drawChartLegend(doc, items, x, y, maxWidth, pdfType);
};

/**
 * Draw payor legend (for Payors chart)
 */
export const drawPayorLegend = (doc, payors, x, y, maxWidth, pdfType = 'printable') => {
  const items = payors.map((payor, idx) => ({
    name: payor.name,
    color: payor.color || DEFAULT_PAYOR_COLORS[idx] || DEFAULT_PAYOR_COLORS[3],
  }));
  // Add "Other" 
  items.push({ name: 'Other Payors', color: DEFAULT_PAYOR_COLORS[3] });
  return drawChartLegend(doc, items, x, y, maxWidth, pdfType);
};

/**
 * Draws an affiliated provider icon (hub icon representation)
 * Since we can't use Material Icons in PDF, we draw a simple indicator
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {Array} color - RGB color array
 * @param {string} pdfType - 'slideshow' or 'printable'
 */
const drawAffiliatedIndicator = (doc, x, y, color, pdfType = 'printable') => {
  const scale = pdfType === 'slideshow' ? 1 : 0.7;
  const circleRadius = 3 * scale;
  const outerRadius = 5 * scale;
  const offset = 4 * scale;
  
  // Draw a small filled circle as the hub indicator
  doc.setFillColor(...color);
  doc.circle(x + offset, y + offset, circleRadius, 'F');
  
  // Draw small lines radiating out (simplified hub icon)
  doc.setDrawColor(...color);
  doc.setLineWidth(0.5 * scale);
  const cx = x + offset;
  const cy = y + offset;
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    doc.line(
      cx + Math.cos(angle) * circleRadius,
      cy + Math.sin(angle) * circleRadius,
      cx + Math.cos(angle) * outerRadius,
      cy + Math.sin(angle) * outerRadius
    );
  }
};

/**
 * Check if we need to add a new page
 */
const checkPageBreak = (doc, currentY, barHeight, barGap, options = {}) => {
  const pdfType = options.pdfType || 'printable';
  const settings = getSettingsByType(pdfType);
  
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const footerHeight = settings.chart.footerHeight;
  const legendHeight = options.showLegend ? settings.chart.legendHeight : 0;
  const bottomMargin = footerHeight + legendHeight + settings.margin.bottom;
  
  if (currentY + barHeight + barGap > pageHeight - bottomMargin) {
    // Draw legend at bottom of current page before adding new page
    if (options.showLegend && options.drawLegend) {
      const effectiveWidth = pageWidth - settings.margin.left - settings.margin.right;
      const legendY = pageHeight - footerHeight - legendHeight;
      options.drawLegend(doc, settings.margin.left, legendY, effectiveWidth);
    }
    
    doc.addPage();
    return settings.margin.top;
  }
  return currentY;
};

/**
 * Draws the Projected Patients horizontal bar chart
 * Matches the ProviderProjectedPatientsChart React component
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} data - Array of claim rows from CLAIMS_PER_PROVIDER
 * @param {Object} options - Chart rendering options
 * @returns {number} - New Y position after chart
 */
export const drawProjectedPatientsChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    showLegend = true,
    patientType = 'ALL_PATIENTS',
    claimsType = 'providers',
    pdfType = 'printable',
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);
  const colors = getColorsByType(pdfType);
  
  const barHeight = settings.chart.barHeight;
  const barGap = settings.chart.barGap;
  const startX = x || settings.margin.left;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const effectiveWidth = width || pageWidth - settings.margin.left - settings.margin.right;
  
  // Labels take left half, bars take right half (minus room for stats)
  const halfPageX = settings.margin.left + (effectiveWidth * 0.5);
  const chartWidth = (effectiveWidth * 0.5) - settings.chart.statsReservedWidth;
  
  // Footer height for calculating available space
  const footerHeight = settings.chart.footerHeight;
  const legendHeight = settings.chart.legendHeight;
  
  let currentY = y;

  // Check if this is any type of captured claims (CAPTURED_CLAIMS or PEDIATRIC_PATIENTS)
  const isCapturedClaims = patientType === 'CAPTURED_CLAIMS' || patientType === 'PEDIATRIC_PATIENTS';

  // Find max value for scaling
  const maxValue = data.reduce((max, row) => {
    const total = parseInt(row.total, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // Determine label for count (patients vs claims)
  const countLabel = isCapturedClaims ? 'claims' : 'patients';

  // Draw each bar
  const rowsToRender = data.slice(0, maxRows);
  
  rowsToRender.forEach((claims, index) => {
    // Check if we need a new page (draw legend at bottom before break)
    currentY = checkPageBreak(doc, currentY, barHeight, barGap, {
      showLegend: showLegend && !isCapturedClaims,
      drawLegend: (doc, x, y, w) => drawLocationTypeLegend(doc, x, y, w, pdfType),
      pdfType,
    });

    const total = parseInt(claims.total, 10) || 0;
    const locationType = claims.location_type || 3; // Default to Office
    
    // Handle both providers and organizations
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    
    if (claimsType === 'organizations') {
      // Organization data structure
      displayName = claims.organization?.name || 'Unknown Organization';
      isAffiliated = claims.affiliated || 
                     claims.organization?.is_affiliated || 
                     (claims.organization?.affiliated?.length > 0);
      // No org name suffix needed for organizations
    } else {
      // Provider data structure
      const firstName = claims.provider?.first_name || '';
      const lastName = claims.provider?.last_name || '';
      displayName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      isAffiliated = claims.affiliated || 
                     claims.provider?.is_affiliated || 
                     (claims.provider?.clients?.length > 0);
      // Get org name for provider
      orgName = claims.organization_name ||
                claims.provider?.location?.organization?.name || 
                claims.location?.organization?.name ||
                '';
    }
    
    // Use blue for Captured Claims (including pediatric), otherwise use location type color
    const barColor = isCapturedClaims 
      ? (pdfType === 'slideshow' ? [55, 95, 180] : [83, 125, 201])  // Blue for captured claims
      : getLocationTypeColor(locationType, colors);
    
    // Calculate bar width
    const barWidthPct = total > 0 ? total / maxValue : 10 / maxValue;
    const barWidth = barWidthPct * chartWidth;

    // Format percentage
    const percentage = claims.aggregateClaimsPercentage 
      ? (claims.aggregateClaimsPercentage * 100).toFixed(2)
      : null;

    // Stats text
    let statsText = total > 0 
      ? `${total.toLocaleString()} ${countLabel}`
      : `<11 ${countLabel}`;
    
    if (percentage !== null) {
      statsText += `  ~${percentage}%`;
      if (index === 0) {
        statsText += ' of Market';
      }
    }

    // === Draw the bar ===
    const barX = halfPageX;  // Bars start at middle of page
    const barY = currentY;
    doc.setFillColor(...barColor);
    doc.roundedRect(barX, barY, Math.max(barWidth, 2), barHeight, 1, 1, 'F');

    // === Draw name (bold) and org (regular) ===
    doc.setFontSize(settings.chart.fontSize.name);
    doc.setTextColor(33, 33, 33);  // Dark black for high contrast

    // Affiliated indicator
    const nameStartX = startX + (isAffiliated ? (pdfType === 'slideshow' ? 12 : 8) : 0);
    if (isAffiliated) {
      drawAffiliatedIndicator(doc, startX, barY + (pdfType === 'slideshow' ? 4 : 2), barColor, pdfType);
    }

    // Draw name in bold
    doc.setFont('helvetica', 'bold');
    const maxNameLen = pdfType === 'slideshow' ? 30 : 25;
    const nameToDraw = displayName.length > maxNameLen ? displayName.substring(0, maxNameLen - 3) + '...' : displayName;
    doc.text(nameToDraw, nameStartX, barY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    
    // Draw org in regular weight (if exists - only for providers)
    if (orgName) {
      const nameWidth = doc.getTextWidth(nameToDraw);
      doc.setFont('helvetica', 'normal');
      let orgToDraw = ` - ${orgName}`;
      // Truncate org if needed
      const maxOrgLen = pdfType === 'slideshow' ? 55 : 50;
      if (orgToDraw.length > maxOrgLen) {
        orgToDraw = orgToDraw.substring(0, maxOrgLen - 3) + '...';
      }
      doc.text(orgToDraw, nameStartX + nameWidth, barY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    }
    
    doc.setFont('helvetica', 'normal');  // Reset to normal

    // === Draw stats (always to the right of bar for clarity) ===
    doc.setFontSize(settings.chart.fontSize.stats);
    doc.setTextColor(60, 60, 60);
    doc.text(statsText, barX + barWidth + 5, barY + barHeight / 2 + 2);

    currentY += barHeight + barGap;
  });

  // Draw legend at the bottom of the last page (unless it's Captured Claims)
  if (showLegend && !isCapturedClaims) {
    const legendY = pageHeight - footerHeight - legendHeight;
    drawLocationTypeLegend(doc, startX, legendY, effectiveWidth, pdfType);
  }

  return currentY + 10;
};

/**
 * Draws affiliation summary badges (like "~0% Affiliated" "~100% Non-Affiliated")
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Object} counts - { total, total_affiliated } from providerCounts
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {Object} options - Options including pdfType
 * @returns {number} - New Y position
 */
export const drawAffiliationSummary = (doc, counts, x, y, options = {}) => {
  if (!counts || !counts.total) return y;
  
  const { pdfType = 'printable' } = options;
  const scale = pdfType === 'slideshow' ? 1 : 0.7;
  
  const affiliatedPct = ((counts.total_affiliated / counts.total) * 100).toFixed(0);
  const nonAffiliatedPct = (100 - affiliatedPct).toFixed(0);
  
  doc.setFontSize(pdfType === 'slideshow' ? 10 : 7);
  doc.setFont('helvetica', 'normal');
  
  const badgeHeight = 22 * scale;
  const badgeWidth1 = 100 * scale;
  const badgeWidth2 = 120 * scale;
  const textPadding = 8 * scale;
  const textY = y + 15 * scale;
  const badgeGap = 10 * scale;
  
  // Affiliated badge
  doc.setFillColor(230, 243, 255); // Light blue background
  doc.rect(x, y, badgeWidth1, badgeHeight, 'F');
  doc.setTextColor(...AFFILIATION_COLORS.affiliated);
  doc.text(`~${affiliatedPct}% Affiliated`, x + textPadding, textY);
  
  // Non-affiliated badge
  doc.setFillColor(255, 235, 238); // Light red/pink background
  doc.rect(x + badgeWidth1 + badgeGap, y, badgeWidth2, badgeHeight, 'F');
  doc.setTextColor(...AFFILIATION_COLORS.nonAffiliated);
  doc.text(`~${nonAffiliatedPct}% Non-Affiliated`, x + badgeWidth1 + badgeGap + textPadding, textY);
  
  // Reset text color
  doc.setTextColor(60, 60, 60);
  
  return y + badgeHeight + (pdfType === 'slideshow' ? 10 : 6);
};

/**
 * Draws the Shared Affiliation stacked horizontal bar chart
 * Shows affiliated % vs non-affiliated % for each provider
 * Bar width is scaled by total shared patients (matching on-screen behavior)
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} data - Array from CLAIMS_SHARED_AFFILIATION
 * @param {Object} options - Chart rendering options
 * @returns {number} - New Y position after chart
 */
export const drawSharedAffiliationChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    showLegend = true,
    pdfType = 'printable',
  } = options;

  // Get settings based on pdfType (consistent with other chart functions)
  const settings = getSettingsByType(pdfType);
  
  const barHeight = settings.chart.barHeight;
  const barGap = settings.chart.barGap;
  const startX = x || settings.margin.left;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const effectiveWidth = width || pageWidth - settings.margin.left - settings.margin.right;
  
  // Use same layout as projected patients: labels on left half, bars on right half
  const halfPageX = settings.margin.left + (effectiveWidth * 0.5);
  const chartWidth = (effectiveWidth * 0.5) - settings.chart.statsReservedWidth;
  
  // Footer height for calculating available space
  const footerHeight = settings.chart.footerHeight;
  const legendHeight = settings.chart.legendHeight;
  
  let currentY = y;

  // Draw legend
  if (showLegend) {
    currentY = drawAffiliationLegend(doc, startX, currentY, effectiveWidth, pdfType);
    currentY += 5;
  }

  // Calculate max shared_patients for scaling bar widths (matching on-screen behavior)
  const rowsToRender = data.slice(0, maxRows);
  let maxShared = 15; // Minimum to avoid division issues
  rowsToRender.forEach((row) => {
    const total = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    maxShared = Math.max(maxShared, total);
  });

  // Draw each bar
  rowsToRender.forEach((row) => {
    // Check if we need a new page
    currentY = checkPageBreak(doc, currentY, barHeight, barGap, {
      showLegend,
      drawLegend: (doc, x, y, w) => drawAffiliationLegend(doc, x, y, w, pdfType),
      pdfType,
    });

    // Handle both flat and nested data structures
    // API returns 'shared_patients' for total and 'shared_patients_affiliated' for affiliated count
    // Some versions may use 'affiliated_totals' instead
    const totalShared = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    
    // Get affiliated count - handle null explicitly (API may return null for 0)
    const rawAffiliated = row.shared_patients_affiliated ?? row.affiliated_totals;
    const affiliatedShared = (rawAffiliated === null || rawAffiliated === undefined) 
      ? 0 
      : (parseInt(rawAffiliated, 10) || 0);
    
    // Calculate percentages for the split within the bar
    const affiliatedPct = totalShared > 0 ? (affiliatedShared / totalShared) * 100 : 0;
    const nonAffiliatedPct = 100 - affiliatedPct;
    
    // Calculate bar width based on total shared patients (matching on-screen chart)
    const barWidthPct = totalShared > 0 ? totalShared / maxShared : 0;
    const totalBarWidth = barWidthPct * chartWidth;
    
    // Provider name - handle both flat and nested structures
    const firstName = row.first_name || row.provider?.first_name || '';
    const lastName = row.last_name || row.provider?.last_name || '';
    const fullName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
    const maxNameLen = pdfType === 'slideshow' ? 24 : 20;
    const displayName = fullName.length > maxNameLen 
      ? fullName.substring(0, maxNameLen - 3) + '...' 
      : (fullName || 'Unknown Provider');

    // Organization name
    const orgName = row.organization_name || '';

    // Provider's own affiliation status (not the same as shared_patients_affiliated)
    const isAffiliated = row.is_affiliated ?? row.affiliated ?? row.provider?.is_affiliated ?? false;

    const barX = halfPageX;
    
    // Draw the scaled bar with affiliated/non-affiliated split
    if (totalBarWidth > 0) {
      // Calculate widths
      const affiliatedWidth = (affiliatedPct / 100) * totalBarWidth;
      const nonAffiliatedWidth = totalBarWidth - affiliatedWidth;
      
      // Draw affiliated portion (blue) first - ensure minimum visible width if > 0%
      if (affiliatedPct > 0) {
        const drawAffiliatedWidth = Math.max(affiliatedWidth, 3); // Minimum 3pt visible
        doc.setFillColor(...AFFILIATION_COLORS.affiliated);
        doc.rect(barX, currentY, drawAffiliatedWidth, barHeight, 'F');
        
        // Draw non-affiliated portion (red/pink) after the blue
        if (nonAffiliatedPct > 0) {
          doc.setFillColor(...AFFILIATION_COLORS.nonAffiliated);
          doc.rect(barX + drawAffiliatedWidth, currentY, totalBarWidth - drawAffiliatedWidth, barHeight, 'F');
        }
      } else {
        // 0% affiliated - draw all pink
        doc.setFillColor(...AFFILIATION_COLORS.nonAffiliated);
        doc.rect(barX, currentY, totalBarWidth, barHeight, 'F');
      }
    }

    // Draw provider name
    doc.setFontSize(settings.chart.fontSize.name);
    
    // All names in black (not colored by affiliation status)
    doc.setTextColor(33, 33, 33);

    const indicatorOffset = pdfType === 'slideshow' ? 12 : 8;
    const nameStartX = startX + (isAffiliated ? indicatorOffset : 0);
    if (isAffiliated) {
      drawAffiliatedIndicator(doc, startX, currentY + (pdfType === 'slideshow' ? 4 : 2), AFFILIATION_COLORS.affiliated, pdfType);
    }

    doc.setFont('helvetica', 'bold');
    doc.text(displayName, nameStartX, currentY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    
    // Draw org name in regular weight (if exists)
    if (orgName) {
      const nameWidth = doc.getTextWidth(displayName);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      let orgToDraw = ` - ${orgName}`;
      // Truncate org if needed
      const maxOrgLen = pdfType === 'slideshow' ? 55 : 50;
      if (orgToDraw.length > maxOrgLen) {
        orgToDraw = orgToDraw.substring(0, maxOrgLen - 3) + '...';
      }
      doc.text(orgToDraw, nameStartX + nameWidth, currentY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    }
    
    doc.setFont('helvetica', 'normal');

    // Draw stats next to the bar: "53 patients  0.00% affiliated"
    doc.setFontSize(settings.chart.fontSize.stats);
    doc.setTextColor(60, 60, 60);
    
    const patientsLabel = totalShared > 0 
      ? `${totalShared.toLocaleString()} patients` 
      : '<11 patients';
    const pctLabel = `${affiliatedPct.toFixed(2)}% affiliated`;
    const statsText = `${patientsLabel}  ${pctLabel}`;
    
    doc.text(statsText, barX + totalBarWidth + 5, currentY + barHeight / 2 + 2);

    currentY += barHeight + barGap;
  });

  return currentY + 10;
};

/**
 * Draws the Shared Affiliation by Service Lines horizontal bar chart
 * Shows percentage of affiliated vs non-affiliated shared patients per service line
 * Similar to drawSharedAffiliationChart but for service line grouping
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} data - Array of service line rows with shared_patients, shared_patients_affiliated
 * @param {Object} options - Chart rendering options
 * @returns {number} - New Y position after chart
 */
export const drawSharedAffiliationByServiceLineChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    showLegend = true,
    pdfType = 'printable',
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);
  
  const barHeight = settings.chart.barHeight;
  const barGap = settings.chart.barGap;
  const startX = x || settings.margin.left;

  const pageWidth = doc.internal.pageSize.width;
  const effectiveWidth = width || pageWidth - settings.margin.left - settings.margin.right;
  
  // Use same layout as other charts: labels on left half, bars on right half
  const halfPageX = settings.margin.left + (effectiveWidth * 0.5);
  const chartWidth = (effectiveWidth * 0.5) - settings.chart.statsReservedWidth;
  
  let currentY = y;

  // Draw legend
  if (showLegend) {
    currentY = drawAffiliationLegend(doc, startX, currentY, effectiveWidth, pdfType);
    currentY += 5;
  }

  // Ensure data is an array
  const dataArray = Array.isArray(data) ? data : [];
  
  // Calculate max shared_patients for scaling bar widths
  const rowsToRender = dataArray.slice(0, maxRows);
  let maxShared = 15; // Minimum to avoid division issues
  rowsToRender.forEach((row) => {
    const total = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    maxShared = Math.max(maxShared, total);
  });

  // Draw each bar
  rowsToRender.forEach((row, index) => {
    // Check if we need a new page
    currentY = checkPageBreak(doc, currentY, barHeight, barGap, {
      showLegend,
      drawLegend: (doc, x, y, w) => drawAffiliationLegend(doc, x, y, w, pdfType),
      pdfType,
    });

    // Get shared patient counts
    const totalShared = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    const rawAffiliated = row.shared_patients_affiliated ?? row.affiliated_totals;
    const affiliatedShared = (rawAffiliated === null || rawAffiliated === undefined) 
      ? 0 
      : (parseInt(rawAffiliated, 10) || 0);
    
    // Calculate percentages for the split within the bar
    const affiliatedPct = totalShared > 0 ? (affiliatedShared / totalShared) * 100 : 0;
    
    // Calculate bar width based on total shared patients
    const barWidthPct = totalShared > 0 ? totalShared / maxShared : 0;
    const totalBarWidth = barWidthPct * chartWidth;
    
    // Service line name - check multiple possible field names
    const serviceLineName = row.name || row.serviceline_name || row.service_line_name || row.label || `Service Line ${index + 1}`;
    const maxNameLen = pdfType === 'slideshow' ? 35 : 30;
    const displayName = serviceLineName.length > maxNameLen 
      ? serviceLineName.substring(0, maxNameLen - 3) + '...' 
      : serviceLineName;

    const barX = halfPageX;
    
    // Draw the scaled bar with affiliated/non-affiliated split
    if (totalBarWidth > 0) {
      const affiliatedWidth = (affiliatedPct / 100) * totalBarWidth;
      
      // Draw affiliated portion (blue) first
      if (affiliatedPct > 0) {
        const drawAffiliatedWidth = Math.max(affiliatedWidth, 3);
        doc.setFillColor(...AFFILIATION_COLORS.affiliated);
        doc.rect(barX, currentY, drawAffiliatedWidth, barHeight, 'F');
        
        // Draw non-affiliated portion (red/pink) after the blue
        if (affiliatedPct < 100) {
          doc.setFillColor(...AFFILIATION_COLORS.nonAffiliated);
          doc.rect(barX + drawAffiliatedWidth, currentY, totalBarWidth - drawAffiliatedWidth, barHeight, 'F');
        }
      } else {
        // 0% affiliated - draw all pink
        doc.setFillColor(...AFFILIATION_COLORS.nonAffiliated);
        doc.rect(barX, currentY, totalBarWidth, barHeight, 'F');
      }
    }

    // Draw service line name on the left
    doc.setFontSize(settings.chart.fontSize.name);
    doc.setTextColor(33, 33, 33);
    doc.setFont('helvetica', 'bold');
    doc.text(displayName, startX, currentY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    doc.setFont('helvetica', 'normal');

    // Draw stats next to the bar: "53 patients  0.00% affiliated"
    doc.setFontSize(settings.chart.fontSize.stats);
    doc.setTextColor(60, 60, 60);
    
    const patientsLabel = totalShared > 0 
      ? `${totalShared.toLocaleString()} patients` 
      : '<11 patients';
    const pctLabel = `${affiliatedPct.toFixed(2)}% affiliated`;
    const statsText = `${patientsLabel}  ${pctLabel}`;
    
    doc.text(statsText, barX + totalBarWidth + 5, currentY + barHeight / 2 + 2);

    currentY += barHeight + barGap;
  });

  return currentY + 10;
};

/**
 * Draws the Payors stacked horizontal bar chart
 * Shows payor mix percentages for each provider
 * All bars are full width, with segments showing each payor's percentage
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} data - Array of claim rows with percent_0, percent_1, percent_2
 * @param {Array} payors - Array of payor objects with name and optional color
 * @param {Object} options - Chart rendering options
 * @returns {number} - New Y position after chart
 */
export const drawPayorChart = (doc, data, payors = [], options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    showLegend = true,
    claimsType = 'providers',
    pdfType = 'printable',
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);
  
  const barHeight = settings.chart.barHeight;
  const barGap = settings.chart.barGap;
  const startX = x || settings.margin.left;

  const pageWidth = doc.internal.pageSize.width;
  const effectiveWidth = width || pageWidth - settings.margin.left - settings.margin.right;
  
  // Use same layout as other charts: labels on left half, bars on right half
  const halfPageX = settings.margin.left + (effectiveWidth * 0.5);
  const chartWidth = (effectiveWidth * 0.5) - 20; // Full width for percentage bars
  
  let currentY = y;

  // Prepare payor colors - use the colors passed from the payors array
  const payorColors = payors.map((payor, idx) => {
    if (payor.color) {
      const rgb = colorToRgb(payor.color);
      if (rgb) return rgb;
    }
    return DEFAULT_PAYOR_COLORS[idx] || DEFAULT_PAYOR_COLORS[3];
  });

  // Draw legend
  if (showLegend && payors.length > 0) {
    const legendPayors = payors.map((p, i) => ({
      name: p.name,
      color: payorColors[i],
    }));
    currentY = drawPayorLegend(doc, legendPayors, startX, currentY, effectiveWidth, pdfType);
    currentY += 5;
  }

  // Draw each bar
  const rowsToRender = data.slice(0, maxRows);
  
  rowsToRender.forEach((claim) => {
    // Check if we need a new page
    currentY = checkPageBreak(doc, currentY, barHeight, barGap, {
      showLegend: showLegend && payors.length > 0,
      drawLegend: (doc, x, y, w) => {
        const legendPayors = payors.map((p, i) => ({
          name: p.name,
          color: payorColors[i],
        }));
        return drawPayorLegend(doc, legendPayors, x, y, w, pdfType);
      },
      pdfType,
    });

    // Get entity name based on type
    let displayName;
    let orgName = '';
    let isAffiliated;
    
    if (claimsType === 'providers') {
      const firstName = claim.provider?.first_name || '';
      const lastName = claim.provider?.last_name || '';
      const fullName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
      const maxNameLen = pdfType === 'slideshow' ? 28 : 24;
      displayName = fullName.length > maxNameLen ? fullName.substring(0, maxNameLen - 3) + '...' : (fullName || 'Unknown');
      isAffiliated = claim.provider?.is_affiliated || claim.provider?.clients?.length > 0;
      
      // Get organization name - check flat field first (from subquery), then nested path
      orgName = claim.organization_name || claim.provider?.location?.organization?.name || '';
    } else {
      const name = claim.organization?.name || '';
      const maxNameLen = pdfType === 'slideshow' ? 28 : 25;
      displayName = name.length > maxNameLen ? name.substring(0, maxNameLen - 3) + '...' : (name || 'Unknown');
      isAffiliated = claim.organization?.is_affiliated || claim.organization?.affiliated?.length > 0;
    }

    const barX = halfPageX;
    let currentBarX = barX;
    
    // Calculate total percentage for determining "Other"
    let totalPct = 0;
    
    // Store segment info for drawing labels after all segments are drawn
    const segments = [];
    
    // Draw each payor segment (full width bar, segments are percentages)
    for (let i = 0; i < payors.length && i < 3; i++) {
      const pct = parseFloat(claim[`percent_${i}`]) || 0;
      totalPct += pct;
      
      const segmentWidth = (pct / 100) * chartWidth;
      if (segmentWidth > 0) {
        doc.setFillColor(...payorColors[i]);
        doc.rect(currentBarX, currentY, segmentWidth, barHeight, 'F');
        segments.push({ x: currentBarX, width: segmentWidth, pct });
        currentBarX += segmentWidth;
      }
    }
    
    // Draw "Other" segment (remainder to make 100%)
    const otherPct = Math.max(0, 100 - totalPct);
    if (otherPct > 0) {
      const otherWidth = (otherPct / 100) * chartWidth;
      doc.setFillColor(...DEFAULT_PAYOR_COLORS[3]); // Gray
      doc.rect(currentBarX, currentY, otherWidth, barHeight, 'F');
      segments.push({ x: currentBarX, width: otherWidth, pct: otherPct, isOther: true });
    }
    
    // Draw percentage labels on segments (white text, only if segment is wide enough)
    doc.setFontSize(settings.chart.fontSize.stats);
    doc.setTextColor(255, 255, 255);
    segments.forEach((seg) => {
      // Only show label if segment is wide enough (at least 30pt)
      if (seg.width >= 30 && seg.pct >= 1) {
        const label = `${Math.round(seg.pct)}%`;
        const labelWidth = doc.getTextWidth(label);
        // Center the label in the segment
        const labelX = seg.x + (seg.width - labelWidth) / 2;
        doc.text(label, labelX, currentY + barHeight / 2 + 3);
      }
    });

    // Draw entity name in black
    doc.setFontSize(settings.chart.fontSize.name);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 33, 33);

    const indicatorOffset = pdfType === 'slideshow' ? 12 : 8;
    const nameStartX = startX + (isAffiliated ? indicatorOffset : 0);
    if (isAffiliated) {
      drawAffiliatedIndicator(doc, startX, currentY + (pdfType === 'slideshow' ? 4 : 2), [32, 131, 244], pdfType);
    }

    doc.text(displayName, nameStartX, currentY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    
    // Draw org name in regular weight (if exists and claimsType is providers)
    if (orgName && claimsType === 'providers') {
      const nameWidth = doc.getTextWidth(displayName);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      let orgToDraw = ` - ${orgName}`;
      // Truncate org if needed - use longer limits
      const maxOrgLen = pdfType === 'slideshow' ? 55 : 50;
      if (orgToDraw.length > maxOrgLen) {
        orgToDraw = orgToDraw.substring(0, maxOrgLen - 3) + '...';
      }
      doc.text(orgToDraw, nameStartX + nameWidth, currentY + barHeight / 2 + (pdfType === 'slideshow' ? 3 : 2));
    }
    
    doc.setFont('helvetica', 'normal');

    currentY += barHeight + barGap;
  });

  return currentY + 10;
};

/**
 * Helper to convert various color formats to RGB array
 */
const colorToRgb = (color) => {
  if (!color) return null;
  
  // Already an RGB array
  if (Array.isArray(color)) {
    return color;
  }
  
  if (typeof color === 'string') {
    // Hex format: #RRGGBB or #RGB
    if (color.startsWith('#')) {
      return hexToRgb(color);
    }
    
    // RGB format: rgb(r, g, b) or rgba(r, g, b, a)
    const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      return [parseInt(rgbMatch[1], 10), parseInt(rgbMatch[2], 10), parseInt(rgbMatch[3], 10)];
    }
  }
  
  return null;
};

/**
 * Helper to convert hex color to RGB array
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
  }
  // Handle shorthand #RGB format
  const shortResult = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
  if (shortResult) {
    return [
      parseInt(shortResult[1] + shortResult[1], 16),
      parseInt(shortResult[2] + shortResult[2], 16),
      parseInt(shortResult[3] + shortResult[3], 16),
    ];
  }
  return [128, 128, 128];
};

export default {
  drawProjectedPatientsChart,
  drawSharedAffiliationChart,
  drawSharedAffiliationByServiceLineChart,
  drawPayorChart,
  drawChartLegend,
  drawLocationTypeLegend,
  drawAffiliationLegend,
  drawPayorLegend,
  drawAffiliationSummary,
  AFFILIATION_COLORS,
  DEFAULT_PAYOR_COLORS,
};